// #656 (Markush M3e): where a repeat unit's brackets and count label go. Pure geometry in paper
// pixels (the caller's space), so "perpendicular" is perpendicular on screen whatever the paper's
// aspect -- the canvas and the SVG export each pass their own positions.

import type { PixelPoint } from './bonds.ts';

/** A repeat unit as the drawing layer needs it; `id` is stable for as long as the unit is. */
export interface RepeatUnitRef {
  readonly id: string;
  readonly atomIds: readonly string[];
  /** `n=1–6`, `n≥1`, ... (markushRepeatUnitMarkers). */
  readonly countLabel: string;
}

export interface RepeatUnitBrackets {
  readonly id: string;
  /** Each bracket as an open polyline: serif, stroke, serif. */
  readonly strokes: readonly (readonly PixelPoint[])[];
  /** Where the count label's text starts (left, baseline-ish centre). */
  readonly label: PixelPoint & { readonly text: string };
  /** The label's tap target, in the same pixel space. */
  readonly labelBox: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
}

const DEFAULT_BOND_PX = 40;
const HALF_HEIGHT = 0.45; // bracket half-length, in bond lengths
const SERIF = 0.15; // serif length, in bond lengths
const LABEL_FONT_PX = 12;

function unitVector(from: PixelPoint, to: PixelPoint): PixelPoint {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  return { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
}

function labelAt(anchor: PixelPoint, text: string): Pick<RepeatUnitBrackets, 'label' | 'labelBox'> {
  const x = anchor.x + 4;
  const y = anchor.y + LABEL_FONT_PX;
  // Generous tap target (min 44x28 before zoom) around roughly-sized text.
  const width = Math.max(44, text.length * LABEL_FONT_PX * 0.62 + 8);
  return {
    label: { x, y, text },
    labelBox: { x: x - 6, y: y - LABEL_FONT_PX - 6, width, height: 28 }
  };
}

/**
 * Brackets for one repeat unit. With two or more bonds leaving the unit, a short square bracket
 * crosses each at its midpoint, perpendicular to it, serifs toward the unit -- the usual
 * -[CH2-CH2]n- depiction; the count goes by the lower end of the rightmost ("closing") bracket.
 * With fewer (a unit at a chain end, or a whole molecule), a pair of brackets encloses the unit's
 * bounding box instead. Undefined when the unit's atoms have no positions.
 */
export function repeatUnitBrackets(
  positions: ReadonlyMap<string, PixelPoint>,
  edges: readonly { readonly source: string; readonly target: string }[],
  unit: RepeatUnitRef
): RepeatUnitBrackets | undefined {
  const inside = new Set(unit.atomIds);
  const corners = unit.atomIds.map((id) => positions.get(id)).filter((point): point is PixelPoint => !!point);
  if (corners.length === 0) return undefined;

  const touching = edges.filter((edge) => inside.has(edge.source) || inside.has(edge.target));
  const lengths = touching.flatMap((edge) => {
    const a = positions.get(edge.source);
    const b = positions.get(edge.target);
    return a && b ? [Math.hypot(b.x - a.x, b.y - a.y)] : [];
  });
  const bond = lengths.length > 0 ? lengths.reduce((sum, length) => sum + length, 0) / lengths.length : DEFAULT_BOND_PX;
  const half = HALF_HEIGHT * bond;
  const serif = SERIF * bond;

  const crossings = touching.flatMap((edge) => {
    const inId = inside.has(edge.source) ? edge.source : edge.target;
    const outId = inId === edge.source ? edge.target : edge.source;
    if (inside.has(outId)) return [];
    const a = positions.get(inId);
    const b = positions.get(outId);
    return a && b ? [{ a, b }] : [];
  });

  if (crossings.length >= 2) {
    const brackets = crossings.map(({ a, b }) => {
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const along = unitVector(a, b); // out of the unit
      const across = { x: -along.y, y: along.x };
      const end1 = { x: mid.x - across.x * half, y: mid.y - across.y * half };
      const end2 = { x: mid.x + across.x * half, y: mid.y + across.y * half };
      const back = (point: PixelPoint) => ({ x: point.x - along.x * serif, y: point.y - along.y * serif });
      return { mid, stroke: [back(end1), end1, end2, back(end2)], lower: end1.y >= end2.y ? end1 : end2 };
    });
    const closing = brackets.reduce((best, bracket) =>
      bracket.mid.x > best.mid.x + 1e-6 || (Math.abs(bracket.mid.x - best.mid.x) <= 1e-6 && bracket.mid.y > best.mid.y)
        ? bracket
        : best
    );
    return {
      id: unit.id,
      strokes: brackets.map((bracket) => bracket.stroke),
      ...labelAt(closing.lower, unit.countLabel)
    };
  }

  const pad = 0.4 * bond;
  const minX = Math.min(...corners.map((point) => point.x)) - pad;
  const maxX = Math.max(...corners.map((point) => point.x)) + pad;
  const minY = Math.min(...corners.map((point) => point.y)) - pad;
  const maxY = Math.max(...corners.map((point) => point.y)) + pad;
  return {
    id: unit.id,
    strokes: [
      [
        { x: minX + serif, y: minY },
        { x: minX, y: minY },
        { x: minX, y: maxY },
        { x: minX + serif, y: maxY }
      ],
      [
        { x: maxX - serif, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: maxX - serif, y: maxY }
      ]
    ],
    ...labelAt({ x: maxX, y: maxY }, unit.countLabel)
  };
}

/** The unit whose count label's tap target contains `point`, if any (last drawn wins). */
export function repeatUnitAtLabel(brackets: readonly RepeatUnitBrackets[], point: PixelPoint): string | undefined {
  for (let index = brackets.length - 1; index >= 0; index -= 1) {
    const { labelBox, id } = brackets[index]!;
    if (
      point.x >= labelBox.x &&
      point.x <= labelBox.x + labelBox.width &&
      point.y >= labelBox.y &&
      point.y <= labelBox.y + labelBox.height
    ) {
      return id;
    }
  }
  return undefined;
}
